import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import TtsPlayer from "../utils/TtsPlayer";
import { nextTtsAction, TtsAction, TtsState } from "../utils/TtsState";
import { AnnotationOverlaps } from "../utils/annotator/annotationsHelper";
import { useFeature } from "./FeatureFlagContext";

export interface TtsDomHandlers {
  root: Element;
  highlightSentence: (range: Range) => void;
  highlightWord: (range: Range) => void;
  clearSentenceHighlight: () => void;
  clearWordHighlight: () => void;
  updateViewerPosition: (range: Range) => void;
  getStartingNode: () => Node;
  onOutOfElements: () => void;
  onOutOfPreviousElements: () => void;
  onTtsStart?: () => void;
  handleAnnotationsInSentence?: (range: Range | null) => AnnotationOverlaps;
}

export interface TtsSettings {
  voice: SpeechSynthesisVoice | null;
  rate: number;
  voiceImageDescriptions: boolean;
  voicePageNumbers: boolean;
}

// State that changes over the player's lifetime. Consumers that read these
// re-render whenever they change. Note: `location` is deliberately NOT here —
// it updates on every spoken sentence but is never rendered, so it lives in a
// ref (read via getLocation) to avoid re-rendering the tree once per sentence.
export interface TtsStateApi {
  state: TtsState;
}

// The action surface. Every function here has a stable identity for the life
// of the provider, so consuming only actions never causes a re-render.
export interface TtsActionsApi {
  isActive: () => boolean;
  getLocation: () => Range | null;
  start: () => void;
  startAtEndOfSection: () => void;
  startFromMouseEvent: (event: MouseEvent) => void;
  pause: () => void;
  resume: () => void;
  skipAhead: () => void;
  skipBack: () => void;
  interrupt: () => void;
  stop: () => void;
  reset: () => void;
  registerDom: (dom: TtsDomHandlers | null) => void;
  setRoot: (root: Element) => void;
  updateSettings: (settings: TtsSettings) => void;
  handlePlayTts: () => void;
}

// The combined surface returned by useTts(), for consumers that want both.
export type TtsContextApi = TtsStateApi & TtsActionsApi;

export const TtsStateContext = createContext<TtsStateApi | null>(null);
export const TtsActionsContext = createContext<TtsActionsApi | null>(null);

export function TtsProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TtsState>(TtsState.Stopped);
  const playerRef = useRef<any>(null);
  const domRef = useRef<TtsDomHandlers | null>(null);
  const annotationsEnabled = useFeature("annotations");

  // Allow the TtsPlayer to close it's AudioContext
  useEffect(() => {
    return () => {
      playerRef.current?.dispose();
      playerRef.current = null;
    };
  }, []);

  // The current sentence range. Updated on every spoken sentence but never
  // rendered (only read at call time in callbacks), so it's a ref rather than
  // state — writing it does not re-render any consumer.
  const locationRef = useRef<Range | null>(null);

  // Mirror `state` into a ref so the stable callbacks below (isActive,
  // handlePlayTts) can read the current value at call time without listing
  // `state` as a dependency — which would otherwise rebuild them every render.
  const stateRef = useRef(state);
  stateRef.current = state;

  const isActive = useCallback(() => {
    return stateRef.current !== TtsState.Stopped;
  }, []);

  const getLocation = useCallback(() => locationRef.current, []);

  const registerDom = useCallback((dom: TtsDomHandlers | null) => {
    domRef.current = dom;
    playerRef.current?.stop();
    playerRef.current?.dispose();
    if (dom === null) {
      playerRef.current = null;
      return;
    }
    playerRef.current = new TtsPlayer({
      root: dom.root,
      annotationsEnabled,
      getState: () => stateRef.current,
      highlightSentence: (range: Range) => {
        locationRef.current = range;
        dom.highlightSentence(range);
      },
      highlightWord: dom.highlightWord,
      clearSentenceHighlight: dom.clearSentenceHighlight,
      clearWordHighlight: dom.clearWordHighlight,
      updateViewerPosition: dom.updateViewerPosition,
      onSpeechEnd: () => {
        setState(TtsState.Stopped);
        locationRef.current = null;
      },
      onOutOfElements: () => {
        dom.onOutOfElements();
      },
      onOutOfPreviousElements: () => {
        dom.onOutOfPreviousElements();
      },
      onBeforeReadingSentence: (range: Range): AnnotationOverlaps | null =>
        dom.handleAnnotationsInSentence?.(range) ?? null,
    });
  }, []);

  const start = useCallback(() => {
    const player = playerRef.current;
    const dom = domRef.current;
    if (!player || !dom) return;
    player.beginSession();
    dom.onTtsStart?.();
    if (player.activeElement && player.currentSentence !== -1) {
      player.play(player.activeElement, player.currentSentence);
    } else {
      player.playFromNode(dom.getStartingNode());
    }
    setState(TtsState.Playing);
  }, []);

  const startAtEndOfSection = useCallback(() => {
    const player = playerRef.current;
    const dom = domRef.current;
    if (!player || !dom) return;
    player.beginSession();
    dom.onTtsStart?.();
    player.playFromEnd();
    setState(TtsState.Playing);
  }, []);

  const startFromMouseEvent = useCallback((event: MouseEvent) => {
    playerRef.current?.beginSession();
    playerRef.current?.playFromMouseEvent(event);
    setState(TtsState.Playing);
  }, []);

  const pause = useCallback(() => {
    playerRef.current?.pause();
    setState(TtsState.Paused);
  }, []);

  const resume = useCallback(() => {
    playerRef.current?.resume();
    setState(TtsState.Playing);
  }, []);

  const skipAhead = useCallback(() => {
    playerRef.current?.ffwd();
  }, []);

  const skipBack = useCallback(() => {
    playerRef.current?.rew();
  }, []);

  const interrupt = useCallback(() => {
    playerRef.current?.interrupt();
    setState(TtsState.Stopped);
    locationRef.current = null;
  }, []);

  const stop = useCallback(() => {
    playerRef.current?.stop();
    setState(TtsState.Stopped);
    locationRef.current = null;
  }, []);

  const reset = useCallback(() => {
    playerRef.current?.reset();
  }, []);

  const setRoot = useCallback((root: Element) => {
    if (playerRef.current) playerRef.current.root = root;
  }, []);

  const updateSettings = useCallback((ttsSettings: TtsSettings) => {
    const player = playerRef.current;
    if (!player) return;
    player.voice = ttsSettings.voice;
    player.rate = ttsSettings.rate;
    player.voiceImageDescriptions = ttsSettings.voiceImageDescriptions;
    player.voicePageNumbers = ttsSettings.voicePageNumbers;
  }, []);

  const handlePlayTts = useCallback(() => {
    switch (nextTtsAction(stateRef.current)) {
      case TtsAction.Pause:
        pause();
        break;
      case TtsAction.Resume:
        resume();
        break;
      case TtsAction.Start:
        start();
        break;
      default:
        break;
    }
  }, [pause, resume, start]);

  // Every dependency here is a stable callback, so `actions` is built once and
  // keeps the same identity for the life of the provider. Action-only consumers
  // never re-render.
  const actions = useMemo<TtsActionsApi>(
    () => ({
      isActive,
      getLocation,
      start,
      startAtEndOfSection,
      startFromMouseEvent,
      pause,
      resume,
      skipAhead,
      skipBack,
      interrupt,
      stop,
      reset,
      registerDom,
      setRoot,
      updateSettings,
      handlePlayTts,
    }),
    [
      isActive,
      getLocation,
      start,
      startAtEndOfSection,
      startFromMouseEvent,
      pause,
      resume,
      skipAhead,
      skipBack,
      interrupt,
      stop,
      reset,
      registerDom,
      setRoot,
      updateSettings,
      handlePlayTts,
    ],
  );

  // Rebuilt only when state actually changes (play/pause/stop transitions).
  const stateValue = useMemo<TtsStateApi>(() => ({ state }), [state]);

  return (
    <TtsActionsContext.Provider value={actions}>
      <TtsStateContext.Provider value={stateValue}>
        {children}
      </TtsStateContext.Provider>
    </TtsActionsContext.Provider>
  );
}

// Subscribe to the changing state (state + location). Re-renders on change.
export function useTtsState(): TtsStateApi {
  const ctx = useContext(TtsStateContext);
  if (!ctx) throw new Error("useTtsState must be used inside <TtsProvider>");
  return ctx;
}

// Subscribe to the stable action surface. Does NOT re-render on state changes.
export function useTtsActions(): TtsActionsApi {
  const ctx = useContext(TtsActionsContext);
  if (!ctx) throw new Error("useTtsActions must be used inside <TtsProvider>");
  return ctx;
}

// Convenience hook returning the full surface. Note: any consumer of useTts()
// re-renders on every state change, since it reads the state context. Prefer
// useTtsActions() when you only need to trigger playback actions.
export function useTts(): TtsContextApi {
  return { ...useTtsActions(), ...useTtsState() };
}
